import { useState, useEffect, useContext } from "react";
import { useAuth } from "../auth/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { PaperGradientBG } from "../kits";
import { NameContext } from "../context/NameContext";

export default function Login() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const { login, loginAsDemoUser } = useAuth();
  const navigate = useNavigate();
  const [showForgotInline, setShowForgotInline] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [emailExists, setEmailExists] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const { name } = useContext(NameContext);
  const forgotHasServerError =
    !forgotSent &&
    /server|netzwerk|spaeter|später/i.test(forgotMessage || "");

  useEffect(() => {
    setEmailExists(null);
  }, [formData.email]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        navigate("/");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);

  const checkEmailExists = async () => {
    const email = formData.email.trim();
    if (email === "" || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setEmailExists(null);
      return;
    }
    setCheckingEmail(true);
    try {
      const res = await fetch(
        `https://www.denisbitter.de/stage01/api/check_email_api.php?email=${encodeURIComponent(email)}`
      );
      if (!res.ok) {
        setEmailExists(null);
      } else {
        const json = await res.json();
        setEmailExists(json.exists);
      }
    } catch (err) {
      console.error("Fehler beim Prüfen der E-Mail:", err);
      setEmailExists(null);
    }
    setCheckingEmail(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDemoLogin = () => {
    loginAsDemoUser();
    navigate("/dashboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const response = await fetch(
        "https://www.denisbitter.de/stage01/api/login_api.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (jsonErr) {
        console.error("JSON Fehler:", jsonErr, "Antwort war:", text);
        setError("Unerwartete Serverantwort.");
        return;
      }
      if (response.ok && data.success && data.token) {
        login(data.token, data.userId, data.mustChange === 1);
        if (data.mustChange === 1) {
          navigate("/reset-password");
        } else {
          navigate("/dashboard");
        }
      } else {
        setError(data.message || "Login fehlgeschlagen.");
      }
    } catch (err) {
      console.error("Netzwerkfehler:", err);
      setError("Server nicht erreichbar.");
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    setForgotMessage("");
    try {
      const res = await fetch("https://www.denisbitter.de/stage01/api/forgot_password.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setForgotSent(true);
        setForgotMessage("reset-link wurde gesendet. bitte postfach pruefen.");
      } else {
        setForgotMessage(data.message || "fehler beim senden.");
      }
    } catch (err) {
      setForgotMessage("serverfehler. bitte spaeter erneut versuchen.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <PaperGradientBG variant="zenBlack">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-md">

          {/* Terminal Window */}
          <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-2xl shadow-black/40 font-mono">
            {/* Title Bar */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
              <button
                type="button"
                onClick={() => navigate("/")}
                aria-label="Login schliessen"
                className="w-3 h-3 rounded-full bg-[#ff5f57] hover:brightness-110 cursor-pointer"
              />
              <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
              <span className="w-3 h-3 rounded-full bg-[#28c840]" />
              <span className="ml-3 text-xs text-[#AC8E66] select-none">login</span>
              <span className="ml-auto text-[11px] text-[#EFEBDC]/35">esc</span>
            </div>

            {/* Content */}
            <form onSubmit={handleSubmit} className="px-5 sm:px-6 py-6 bg-[#151515]">

              {/* Demo Hint */}
              <p className="text-sm text-[#EFEBDC]/50 mb-1">
                <span className="text-[#AC8E66]">$</span> demo --info
              </p>
              <div className="ml-3 mb-5 text-xs text-[#EFEBDC]/60 leading-relaxed space-y-1">
                <p>
                  <span className="text-[#AC8E66]/60">&gt;</span> moin{name ? `, ${name}` : ""}.
                  kurs cockpit testen?
                </p>
                <p>
                  <span className="text-[#AC8E66]/60">&gt;</span>{" "}
                  <span
                    onClick={handleDemoLogin}
                    className="text-[#AC8E66] hover:text-[#AC8E66]/80 cursor-pointer underline underline-offset-2 transition-colors"
                  >
                    [{name || "demo"} --cockpit]
                  </span>
                  {" "}ohne anmeldung.
                </p>
              </div>

              {/* Login Prompt */}
              <p className="text-sm text-[#EFEBDC]/50 mb-4">
                <span className="text-[#AC8E66]">$</span> auth --login
              </p>

              {/* Email */}
              <div className="mb-3">
                <label className="text-[10px] text-[#EFEBDC]/30 uppercase tracking-wider mb-1 block">email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="user@email.de"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={checkEmailExists}
                  required
                  className="w-full bg-[#1c1c1c] border border-[#AC8E66]/20 rounded px-3 py-2.5 text-sm text-[#EFEBDC] placeholder-[#EFEBDC]/20 font-mono focus:outline-none focus:border-[#AC8E66]/50 transition-colors"
                />
              </div>
              {checkingEmail && (
                <p className="text-xs text-[#EFEBDC]/40 mb-2 ml-1">
                  <span className="text-[#AC8E66]/60">&gt;</span> pruefe email ...
                </p>
              )}
              {emailExists === false && (
                <p className="text-xs text-red-400/80 mb-2 ml-1">
                  <span className="text-red-400/60">&gt;</span> email nicht gefunden.{" "}
                  <Link to="/register" className="text-[#AC8E66] underline underline-offset-2">
                    registrieren →
                  </Link>
                </p>
              )}

              {/* Password */}
              <div className="mb-5">
                <label className="text-[10px] text-[#EFEBDC]/30 uppercase tracking-wider mb-1 block">passwort</label>
                <input
                  type="password"
                  name="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#1c1c1c] border border-[#AC8E66]/20 rounded px-3 py-2.5 text-sm text-[#EFEBDC] placeholder-[#EFEBDC]/20 font-mono focus:outline-none focus:border-[#AC8E66]/50 transition-colors"
                />
              </div>

              {/* Error */}
              {error && (
                <p className="text-xs text-red-400/80 mb-3 ml-1">
                  <span className="text-red-400/60">&gt;</span> error: {error}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                className="w-full py-2.5 text-sm font-mono text-[#AC8E66] bg-[#1c1c1c] border border-[#AC8E66]/30 rounded hover:border-[#AC8E66]/60 hover:bg-[#1e1e1e] transition-all cursor-pointer"
              >
                [ einloggen → ]
              </button>

              {/* Back */}
              <Link to="/" className="block mt-3">
                <button
                  type="button"
                  className="w-full py-2 text-xs font-mono text-[#EFEBDC]/40 hover:text-[#EFEBDC]/70 transition-colors cursor-pointer"
                >
                  [ zurueck → ]
                </button>
              </Link>

              {/* Forgot Password */}
              <div className="mt-5 pt-4 border-t border-[#AC8E66]/10">
                {!showForgotInline ? (
                  <p
                    className="text-xs text-[#EFEBDC]/30 hover:text-[#AC8E66] cursor-pointer transition-colors font-mono text-center"
                    onClick={() => {
                      setShowForgotInline(true);
                      setForgotSent(false);
                      setForgotEmail(formData.email || "");
                      setForgotMessage("");
                    }}
                  >
                    passwort vergessen?
                  </p>
                ) : (
                  <div className="mt-1 rounded border border-[#AC8E66]/20 bg-[#101010] p-3">
                    <p className="text-sm text-[#EFEBDC]/50 mb-2">
                      <span className="text-[#AC8E66]">$</span> auth --forgot
                    </p>
                    <p className="text-xs text-[#EFEBDC]/55 mb-3">
                      <span className="text-[#AC8E66]/60">&gt;</span> gib deine e-mail ein ...
                    </p>

                    {forgotSent ? (
                      <p className="text-xs text-green-400 mb-3">
                        <span className="text-green-400/70">&gt;</span> {forgotMessage}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="email"
                          name="forgotEmail"
                          placeholder="deine@email.de"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          required
                          className="w-full bg-[#1c1c1c] border border-[#AC8E66]/20 rounded px-3 py-2.5 text-sm text-[#EFEBDC] placeholder-[#EFEBDC]/20 font-mono focus:outline-none focus:border-[#AC8E66]/50 transition-colors"
                        />
                        {forgotMessage && (
                          <p className="text-xs text-red-400/80">
                            <span className="text-red-400/60">&gt;</span> {forgotMessage}
                          </p>
                        )}
                        {forgotHasServerError && (
                          <a
                            href={`mailto:saghallo@denisbitter.de?subject=${encodeURIComponent("Reset-Link Problem - denisbitter.de")}&body=${encodeURIComponent(
                              `Hi Denis,\nich kann mein Passwort aktuell nicht zuruecksetzen.\nEmail: ${forgotEmail || "nicht angegeben"}`
                            )}`}
                            className="block w-full py-2 text-xs font-mono text-[#EFEBDC] bg-[#1c1c1c] border border-[#AC8E66]/20 rounded hover:border-[#AC8E66]/50 hover:bg-[#1e1e1e] transition-all text-center"
                          >
                            [ email an mich → ]
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={handleForgotSubmit}
                          disabled={forgotLoading}
                          className={`w-full py-2 text-xs font-mono text-[#AC8E66] bg-[#1c1c1c] border border-[#AC8E66]/30 rounded hover:border-[#AC8E66]/60 hover:bg-[#1e1e1e] transition-all ${
                            forgotLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                          }`}
                        >
                          {forgotLoading ? "[ sende reset-link ... ]" : "[ reset-link senden → ]"}
                        </button>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotInline(false);
                        setForgotSent(false);
                        setForgotMessage("");
                      }}
                      className="w-full mt-2 py-1 text-xs font-mono text-[#EFEBDC]/35 hover:text-[#EFEBDC]/60 transition-colors cursor-pointer"
                    >
                      [ zurueck login ]
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>

        </div>
      </div>
    </PaperGradientBG>
  );
}
